import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Leaf, Hammer, Award, Compass, Heart, Recycle } from 'lucide-react';
import Breadcrumb from '../components/Breadcrumb';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';

const HERO_IMG   = 'https://images.unsplash.com/photo-1502980426475-b83966705988?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800';
const STUDIO_IMG = 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200';
const CRAFT_IMG  = 'https://images.unsplash.com/photo-1554631830-92b02aafbfcd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200';
const MILL_IMG   = 'https://images.unsplash.com/photo-1554941829-202a0b2403b8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600';

// Repeating section shell
function StorySection({ n, eyebrow, title, body, image, reverse = false, id }) {
  return (
    <section id={id} className="container py-24 md:py-32 grid gap-12 md:grid-cols-12 items-center scroll-mt-32">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.8, ease: [0.85, 0, 0.15, 1] }}
        className={`md:col-span-5 ${reverse ? 'md:col-start-8' : ''}`}
      >
        <p className="overline tabular text-muted-foreground">{n}</p>
        <p className="mt-4 overline text-muted-foreground">{eyebrow}</p>
        <h2 className="mt-4 font-display text-4xl md:text-6xl leading-[1.02]">{title}</h2>
        <div className="mt-6 space-y-5 text-muted-foreground leading-relaxed">
          {body.map((p, i) => <p key={i}>{p}</p>)}
        </div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.9, ease: [0.85, 0, 0.15, 1] }}
        className={`md:col-span-7 aspect-[4/5] md:aspect-[3/2] overflow-hidden bg-muted ${reverse ? 'md:col-start-1 md:row-start-1' : ''}`}
      >
        <img src={image} alt="" className="h-full w-full object-cover" />
      </motion.div>
    </section>
  );
}

const PRINCIPLES = [
  { Icon: Compass,  h: 'Slow by design',    d: 'Small collections, made in trusted workshops, released twice a year.' },
  { Icon: Hammer,   h: 'Built to be kept',  d: 'Reinforced seams, natural fibres, and a promise to repair for the life of the piece.' },
  { Icon: Leaf,     h: 'Cleaner sourcing',  d: 'GOTS-certified cotton, RWS wool, recycled poly where performance demands it.' },
  { Icon: Recycle,  h: 'Circular thinking', d: 'Free hemming in the first year and a take-back programme for pieces you\'ve outgrown.' },
  { Icon: Award,    h: 'Fair, always',      d: 'Every mill we work with is audited on wages, hours, and worker voice.' },
  { Icon: Heart,    h: 'For a lifetime',    d: 'We design pieces to outlast season, trend, and even us.' },
];

export default function About() {
  return (
    <div data-testid="page-about">
      <SEO
        title="About"
        description="The house of Evermark — brand story, mission, sustainability, and quality commitment. A legacy in every mark."
        path="/about"
        breadcrumb={[{ to: '/', label: 'Home' }, { label: 'About' }]}
      />
      {/* Hero */}
      <section className="relative h-[80vh] min-h-[520px] overflow-hidden border-b border-border">
        <img src={HERO_IMG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/30" />
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center text-white px-6">
          <p className="overline">The House of Evermark</p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: [0.85, 0, 0.15, 1] }}
            className="mt-6 font-display text-6xl md:text-8xl leading-[0.9]"
          >
            A legacy in every mark.
          </motion.h1>
          <p className="mt-6 font-display italic text-xl md:text-2xl max-w-xl">
            Designed for Everyday Confidence.
          </p>
        </div>
      </section>

      {/* Section jump nav */}
      <div className="border-b border-border sticky top-16 md:top-28 z-20 bg-background/90 backdrop-blur-xl">
        <div className="container flex flex-wrap gap-x-8 gap-y-2 py-4 overline text-muted-foreground">
          <Breadcrumb items={[{ to: '/', label: 'Home' }, { label: 'About' }]} />
          <span className="opacity-30 hidden md:inline">·</span>
          <a href="#story" className="link-underline">Story</a>
          <a href="#mission" className="link-underline">Mission</a>
          <a href="#vision" className="link-underline">Vision</a>
          <a href="#sustainability" className="link-underline">Sustainability</a>
          <a href="#manufacturing" className="link-underline">Manufacturing</a>
          <a href="#quality" className="link-underline">Quality</a>
        </div>
      </div>

      <StorySection
        id="story"
        n="01"
        eyebrow="Our story"
        title="It began with one coat."
        image={STUDIO_IMG}
        body={[
          'Evermark was founded in 2026 with a single question: what stays with you? We began by making one coat we could keep for a decade — the kind of piece that would grow into you rather than away from you.',
          'From that one coat came a shirt, a knit, a pair of trousers. Each piece designed in our studio, made with mills and makers we have visited, and released only when we\'re certain it will last.',
          'We keep the collection small on purpose. We answer to our customers, our makers, and to the material — nothing else.',
        ]}
      />

      <StorySection
        id="mission"
        n="02"
        eyebrow="Our mission"
        title="To make clothes that outlast their season."
        image={CRAFT_IMG}
        reverse
        body={[
          'We exist to slow the wardrobe. To design pieces so quietly considered that they earn their place across seasons, weathers, and moods.',
          'Our mission is not simply to make better clothes; it is to make fewer of them, more thoughtfully, in a way that respects the hands that made them and the land they came from.',
        ]}
      />

      <StorySection
        id="vision"
        n="03"
        eyebrow="Our vision"
        title="A wardrobe of ten pieces, worn one hundred ways."
        image={MILL_IMG}
        body={[
          'We imagine a future where clothing is bought less, kept longer, and passed on with pride. Where the mark on a garment is a story, not a logo.',
          'Ten pieces, one hundred ways. A wardrobe built the way a house is built: with intention, with patience, and with the expectation that it will be here in a decade.',
        ]}
      />

      {/* Principles */}
      <section id="principles" className="border-y border-border">
        <div className="container py-24 md:py-32">
          <div className="mb-14 grid md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-6">
              <p className="overline text-muted-foreground">Our principles</p>
              <h2 className="mt-4 font-display text-5xl md:text-6xl leading-[0.95]">Six quiet promises.</h2>
            </div>
            <p className="md:col-span-6 md:justify-self-end max-w-md text-muted-foreground">
              We publish these to hold ourselves to them. They are the plainest description of how Evermark chooses to work.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PRINCIPLES.map(({ Icon, h, d }, i) => (
              <motion.article
                key={h}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.7, delay: (i % 3) * 0.08, ease: [0.85, 0, 0.15, 1] }}
                className="border border-border p-8 hover:border-foreground transition-colors duration-500"
              >
                <Icon className="h-5 w-5 opacity-80" strokeWidth={1.25} />
                <h3 className="mt-6 font-display text-2xl">{h}</h3>
                <p className="mt-3 text-muted-foreground leading-relaxed">{d}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <StorySection
        id="sustainability"
        n="04"
        eyebrow="Sustainability"
        title="Cleaner is not enough. Kinder is the aim."
        image={CRAFT_IMG}
        body={[
          'We work only with certified organic cotton (GOTS), responsibly sourced wool (RWS), regenerative linen, and recycled polyester where technical performance demands it. We refuse PFC treatments, virgin polyester, and any material we cannot trace to a mill we\'ve visited.',
          'Every year we publish a public materials report — what we bought, from where, and what percentage was certified. When we fall short, we say so.',
          'Beyond the fibre, we design for repair: every piece can be sent back to our workshop to be hemmed, patched, or restored. For the life of the garment.',
        ]}
      />

      <StorySection
        id="manufacturing"
        n="05"
        eyebrow="Manufacturing"
        title="Twelve workshops. Four generations. One standard."
        image={STUDIO_IMG}
        reverse
        body={[
          'We produce in twelve workshops across Italy, Portugal, Japan, Scotland, and the United States. Some have been in the same family for a hundred and fifty years. Most we\'ve worked with since our first collection.',
          'Every partner is audited annually on wages, hours, worker voice, water use, and waste. We publish the list on request. If a supplier changes hands or changes standards, we reconsider the relationship — always in that order.',
          'We do not produce more than we can sell. Small runs. Made slowly. No pre-season overproduction.',
        ]}
      />

      <StorySection
        id="quality"
        n="06"
        eyebrow="Quality commitment"
        title="If it doesn\u2019t last a decade, we haven\u2019t made it right."
        image={MILL_IMG}
        body={[
          'Every piece is inspected twice — once at the mill, once at our atelier — before it ships. Seams are pulled, buttons tested, colour checked in daylight.',
          'Our promise is simple: if a piece fails from a manufacturing fault within ten years, we\'ll repair or replace it. We call it the Ten-Year Mark, and it is the standard we hold ourselves to.',
          'You will find the Mark on every hangtag. It is our signature and our accountability.',
        ]}
      />

      {/* CTA */}
      <section className="container py-24 md:py-40 text-center border-t border-border">
        <p className="overline text-muted-foreground">The wardrobe, kept.</p>
        <h2 className="mt-6 font-display text-5xl md:text-7xl">Begin your Evermark.</h2>
        <div className="mt-10 flex flex-wrap gap-4 justify-center">
          <Button asChild size="lg" data-testid="about-shop-cta"><Link to="/shop">Shop the Catalog</Link></Button>
          <Button asChild variant="outline" size="lg" data-testid="about-blog-cta"><Link to="/blog">Read the Journal</Link></Button>
        </div>
      </section>
    </div>
  );
}
